import DashboardOrchService from "../Services/DashboardOrchService";
import docEditorServiceApi from "../Services/doc-editor-service-api";
import jobManagerApi from "../Services/job-manager-api";
import type { DocEditorResponse } from "../types/DocEditorResponse";
import type { JobStatusResponse } from "../types/JobStatusResponse";
import {
  blobSignature,
  jobCounter,
  pollingIntervalInMilliSeconds,
  sasTokenExpiryHours,
} from "../utils/general-config";

export const GetMusic = async (
  data: Record<string, unknown>,
): Promise<DocEditorResponse> => {
  return docEditorServiceApi.MusicGeneration(data);
};

export const SeparateMusicStems = async (
  data: Record<string, unknown>,
): Promise<DocEditorResponse> => {
  return docEditorServiceApi.StemSeparation(data);
};

export const generateSasUriToken = async (): Promise<string> => {
  try {
    const response =
      (await DashboardOrchService.generateSasUriTokenForContainer(
        sasTokenExpiryHours,
      )) as unknown as string;

    if (response) {
      const tokenUrl = new URL(response);
      return tokenUrl.search;
    }

    return blobSignature();
  } catch {
    return blobSignature();
  }
};

export const DocOperationJobStatusWithInterval = async (
  jobId: string,
  signal?: AbortSignal,
  maxAttempts: number = jobCounter,
): Promise<JobStatusResponse> => {
  let timer = 0;
  let intervalId: ReturnType<typeof setInterval> | undefined;

  return new Promise<JobStatusResponse>((resolve, reject) => {
    const checkStatus = async () => {
      if (signal?.aborted) {
        if (intervalId) clearInterval(intervalId);
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }

      try {
        timer += 1;
        const responseStatus = await getDocOperationJobStatus(jobId);

        if (responseStatus?.status === 30) {
          if (intervalId) clearInterval(intervalId);
          resolve(responseStatus as JobStatusResponse);
          return;
        }

        if (responseStatus?.status === 40) {
          if (intervalId) clearInterval(intervalId);
          // reject(new Error("Job status indicates failure."));
          reject(new Error("Please try again."));
          return;
        }

        if (timer >= maxAttempts) {
          if (intervalId) clearInterval(intervalId);
          // reject(new Error("Job status check timed out."));
          reject(new Error("Please try again."));
        }
      } catch (error) {
        if (intervalId) clearInterval(intervalId);
        reject(error);
      }
    };

    void checkStatus();
    intervalId = setInterval(() => {
      void checkStatus();
    }, pollingIntervalInMilliSeconds);
  });
};

const getDocOperationJobStatus = async (
  jobId: string,
): Promise<JobStatusResponse | null> => {
  try {
    const jobStatus = await jobManagerApi.getJobStatus(jobId);
    if (jobStatus?.jobID) {
      return jobStatus;
    }
    return null;
  } catch (error) {
    console.error("Error getting job status:", error);
    return null;
  }
};
